import { ArrowLeft } from 'lucide-react';
import { Button, Avatar, EventCover, EventMeetingPoints, showToast, Text, type MeetingPointData } from '../../../shared/components/sketchy';
import { useFestipodData } from '../../../shared/context/FestipodDataContext';
import { useNavigate, useParams } from '../../../app/router';

export function EventDetailScreen() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const {
    getEvent,
    currentUser,
    isParticipating,
    joinEvent,
    leaveEvent,
    getEventParticipants,
    getEventMeetingPoints,
    getEventOwnership,
  } = useFestipodData();

  const event = eventId ? getEvent(eventId) : undefined;
  const joined = eventId ? isParticipating(eventId) : false;
  const participants = eventId ? getEventParticipants(eventId) : [];
  const meetingPointsRaw = eventId ? getEventMeetingPoints(eventId) : [];

  const meetingPoints: MeetingPointData[] = meetingPointsRaw.map(mp => ({
    id: mp.id,
    title: mp.location,
    when: mp.time,
    duration: '~60 min',
    lieu: mp.location,
  }));

  // EDITING IS OWNING. Only a document's owner writes to it, and nothing delegates
  // a write, so "may I edit this event" is answered entirely by whether this
  // session owns the event's document — no permission call, no extra read.
  //
  // The third answer, `'unknown'`, is rendered as a DISABLED affordance rather
  // than resolved either way. Hiding it would be the worst outcome: an owner
  // would be told, silently and wrongly, that their own event is not theirs, with
  // nothing on screen to suggest otherwise. Showing it live would be the opposite
  // lie — a control that promises an edit it may not be able to make, and only
  // says so after the person has typed. Visible-but-inert says the true thing:
  // the answer is still coming.
  const ownership = eventId ? getEventOwnership(eventId) : 'unknown';
  const canEdit = ownership === 'mine';
  // Preview list shows the OTHER participants (deliberate — the total is in the
  // header count; the full list at "Voir tous les participants" shows everyone).
  // Compare on the PROFILE id: `currentUser.id` is the resolved profile NURI, the
  // same space as `p.id` — whereas `currentUserId` is the `urn:festipod:user:`
  // principal, which never equals a profile id in connected mode (so the old
  // `p.id !== currentUserId` failed to drop self, leaking it in as "1 unknown").
  const knownParticipants = participants.filter(p => p.id !== currentUser?.id);

  const handleToggleJoin = () => {
    if (!eventId) return;
    // THE CONFIRMATION FOLLOWS THE WRITE. It used to be shown on the spot, before
    // the call had settled, so a sign-up that wrote nothing still read as
    // « Tu participes ». The screen's own list flips immediately anyway (the data
    // layer's optimistic overlay), so nothing is lost by waiting for the truth.
    const confirmed = (message: string, tone: 'success' | 'info') => () => showToast(message, tone);
    const failed = (message: string) => (err: unknown) => {
      console.error('[EventDetail] participation write failed:', err);
      showToast(message, 'error');
    };
    if (joined) {
      void Promise.resolve(leaveEvent(eventId))
        .then(confirmed('Participation annulée', 'info'))
        .catch(failed("La désinscription n'a pas pu être enregistrée"));
    } else {
      void Promise.resolve(joinEvent(eventId))
        .then(confirmed('Tu participes à cet événement', 'success'))
        .catch(failed("L'inscription n'a pas pu être enregistrée"));
    }
  };

  if (!event) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}><ArrowLeft size={20} /></button>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Événement</span>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text>Événement non trouvé</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#333', padding: 0, display: 'flex', alignItems: 'center' }}><ArrowLeft size={20} /></button>
          <div style={{ flex: 1 }}>
            <div className="user-content" style={{ fontSize: 18, fontWeight: 700 }}>{event.title}</div>
            {event.distance != null && (
              <div style={{ fontSize: 12, color: '#888' }}>{event.distance} km</div>
            )}
          </div>
          {canEdit && (
            <span
              onClick={() => navigate(`/events/${eventId}/edit`)}
              title="Modifier l'événement"
              style={{ cursor: 'pointer', fontSize: 18, color: '#888' }}
            >✎</span>
          )}
          {ownership === 'unknown' && (
            // DELIBERATELY NOT A ✎. The slot stays occupied, so an owner is never
            // silently told the event is not theirs — but a pending control must
            // not look like the actionable one it is not: a greyed-out twin of the
            // pencil reads as "edit, broken" and invites a click that does nothing.
            // A distinct mark reads as "still working it out", which is the truth.
            <span
              aria-busy="true"
              aria-label="Vérification de vos droits de modification"
              title="Vérification de vos droits de modification…"
              style={{ cursor: 'default', fontSize: 18, color: '#ddd' }}
            >⋯</span>
          )}
        </div>

        <div style={{ padding: '0 16px 12px' }}>
          <EventCover eventId={event.id} height={100} />
        </div>

        <div style={{ margin: '0 16px 12px', padding: 14, background: '#fafafa', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 15 }}>📅</span>
            <div>
              <div className="user-content" style={{ fontSize: 13, fontWeight: 600 }}>{event.date}</div>
              {event.startTime && (
                <div style={{ fontSize: 12, color: '#888' }}>
                  {event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 15 }}>📍</span>
            <span className="user-content" style={{ fontSize: 13 }}>{event.location}</span>
          </div>
          {event.description && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 15 }}>📝</span>
              <span className="user-content" style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                {event.description}
              </span>
            </div>
          )}
        </div>

        <div style={{ margin: '4px 16px 16px', display: 'flex', gap: 8 }}>
          <Button
            variant={joined ? 'green' : 'primary'}
            onClick={handleToggleJoin}
            style={{ flex: 1, padding: '12px 0' }}
          >
            {joined ? '✓ Je participe' : "J'y serai"}
          </Button>
          {joined && (
            <Button
              onClick={() => navigate(`/events/${eventId}/invite`)}
              style={{ flex: 1, padding: '12px 0' }}
            >
              Inviter
            </Button>
          )}
        </div>

        {meetingPoints.length > 0 && (
          <div style={{ padding: '0 16px 8px' }}>
            <EventMeetingPoints
              points={meetingPoints}
              joinedIds={new Set()}
              onToggle={() => {}}
              expanded
            />
          </div>
        )}

        <div style={{ padding: '0 16px 8px' }}>
          <button
            onClick={() => navigate(`/events/${eventId}/meeting-points`)}
            style={{ width: '100%', padding: 12, border: '2px dashed #ddd', borderRadius: 12, background: 'none', fontSize: 13, fontWeight: 600, color: '#999', cursor: 'pointer', marginTop: 4, fontFamily: 'var(--font-app)' }}
          >
            + Proposer un point de rencontre
          </button>
        </div>

        <div style={{ padding: '16px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
            Participants ({event.participantCount})
          </div>
          {knownParticipants.map((p) => (
            <div
              key={p.id}
              onClick={() => navigate(`/users/${p.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
            >
              <Avatar initials={p.initials} size={38} color="#2B6CB0" />
              <div style={{ flex: 1 }}>
                <div className="user-content" style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                {p.username && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{p.username}</div>
                )}
              </div>
            </div>
          ))}
          {knownParticipants.length < event.participantCount && (
            <div
              style={{ marginTop: 12, padding: 12, background: '#f9f9f9', borderRadius: 12, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => navigate(`/events/${eventId}/participants`)}
            >
              <span style={{ fontSize: 12, color: '#999' }}>Voir tous les participants →</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
